import React, {
  type ReactNode,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import ReportingChartRenderer, {
  canRenderReportingChart,
} from 'src/agent/components/ReportingChartRenderer/ReportingChartRenderer'
import type {
  ReportingArtifact,
  ReportingChartSpec,
  ReportingDataCell,
  ReportingDataset,
} from 'src/agent/reporting/reportingPresentation.types'
import { useTranslate } from 'src/localization/Trns'

import {
  CanvasHeader,
  CanvasHeading,
  CanvasShell,
  DataTable,
  DataTableWrapper,
  EmptyState,
  EmptyStateHeading,
  Tab,
  TableCaption,
  TabList,
  TabPanel,
} from './ReportingCanvas.style'

export type ReportingCanvasTab = 'visualization' | 'data'

export interface ReportingCanvasProps {
  artifact: ReportingArtifact | null | undefined
  header?: ReactNode
  presentation?: 'card' | 'embedded'
  fitToContainer?: boolean
  renderVisualization?: (
    chartSpec: ReportingChartSpec,
    dataset: ReportingDataset,
  ) => ReactNode
}

const valueForCell = (value: ReportingDataCell): string =>
  value === null ? 'null' : String(value)

const hasVisualization = (
  chartSpec: ReportingChartSpec | null | undefined,
  dataset: ReportingDataset,
): chartSpec is ReportingChartSpec => {
  return canRenderReportingChart(chartSpec, dataset)
}

const tableColumnsFor = (
  chartSpec: ReportingChartSpec | null | undefined,
  dataset: ReportingDataset,
): typeof dataset.columns => {
  const table = chartSpec?.table
  if (!table || typeof table !== 'object') return dataset.columns
  const configuredColumns = (table as Record<string, unknown>).columns
  if (!Array.isArray(configuredColumns)) return dataset.columns
  const columnsByKey = new Map(
    dataset.columns.map((column) => [column.key, column]),
  )
  const columns = configuredColumns
    .filter((key): key is string => typeof key === 'string')
    .map((key) => columnsByKey.get(key))
    .filter((column): column is (typeof dataset.columns)[number] =>
      Boolean(column),
    )
  return columns.length > 0 ? columns : dataset.columns
}

const ReportingDataTable: React.FC<{
  chartSpec?: ReportingChartSpec | null
  dataset: ReportingDataset
  hideCaption?: boolean
}> = ({ chartSpec, dataset, hideCaption = false }) => {
  const translate = useTranslate()
  const columns = tableColumnsFor(chartSpec, dataset)

  return (
    <DataTableWrapper>
      <DataTable
        aria-label={hideCaption ? translate('Exact report data') : undefined}
      >
        {!hideCaption && (
          <TableCaption>{translate('Exact report data')}</TableCaption>
        )}
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.key} scope="col">
                {column.label ?? column.key}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataset.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {columns.map((column) => (
                <td key={column.key}>{valueForCell(row[column.key])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </DataTable>
    </DataTableWrapper>
  )
}

const ReportingCanvas: React.FC<ReportingCanvasProps> = ({
  artifact,
  header,
  presentation = 'card',
  fitToContainer = false,
  renderVisualization,
}) => {
  const translate = useTranslate()
  const isEmbedded = presentation === 'embedded'
  const canVisualize = artifact
    ? hasVisualization(artifact.chartSpec, artifact.dataset)
    : false
  const defaultTab: ReportingCanvasTab = canVisualize ? 'visualization' : 'data'
  const [selectedTab, setSelectedTab] = useState<ReportingCanvasTab>(defaultTab)
  const tabRefs = useRef<
    Partial<Record<ReportingCanvasTab, HTMLButtonElement>>
  >({})
  const idPrefix = useId()

  useEffect(() => {
    setSelectedTab(defaultTab)
  }, [artifact?.id, defaultTab])

  if (!artifact) {
    const content = (
      <>
        {!isEmbedded && (
          <CanvasHeading id={`${idPrefix}-heading`}>
            {translate('Report canvas')}
          </CanvasHeading>
        )}
        <EmptyState>
          <EmptyStateHeading>
            {translate('Your report will appear here')}
          </EmptyStateHeading>
          <p>
            {translate('Ask the report assistant for a report to get started.')}
          </p>
        </EmptyState>
      </>
    )

    if (isEmbedded) return content

    return (
      <CanvasShell aria-label={translate('Report canvas')}>
        {header && <CanvasHeader>{header}</CanvasHeader>}
        {content}
      </CanvasShell>
    )
  }

  const tabs: readonly ReportingCanvasTab[] = canVisualize
    ? ['visualization', 'data']
    : ['data']

  const focusTab = (tab: ReportingCanvasTab): void => {
    setSelectedTab(tab)
    tabRefs.current[tab]?.focus()
  }

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    tab: ReportingCanvasTab,
  ): void => {
    if (tabs.length < 2) return
    const tabIndex = tabs.indexOf(tab)
    let nextTab: ReportingCanvasTab | undefined
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextTab = tabs[(tabIndex + 1) % tabs.length]
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextTab = tabs[(tabIndex - 1 + tabs.length) % tabs.length]
    } else if (event.key === 'Home') {
      nextTab = tabs[0]
    } else if (event.key === 'End') {
      nextTab = tabs[tabs.length - 1]
    }
    if (!nextTab) return
    event.preventDefault()
    focusTab(nextTab)
  }

  const tabId = (tab: ReportingCanvasTab): string => `${idPrefix}-${tab}-tab`
  const panelId = (tab: ReportingCanvasTab): string =>
    `${idPrefix}-${tab}-panel`
  const chartSpec = artifact.chartSpec

  const content = (
    <>
      {!isEmbedded && (
        <CanvasHeading id={`${idPrefix}-heading`}>
          {artifact.title}
        </CanvasHeading>
      )}
      <TabList role="tablist" aria-label={translate('Report result views')}>
        {tabs.map((tab) => (
          <Tab
            key={tab}
            ref={(element): void => {
              tabRefs.current[tab] = element ?? undefined
            }}
            type="button"
            role="tab"
            id={tabId(tab)}
            aria-controls={panelId(tab)}
            aria-selected={selectedTab === tab}
            tabIndex={selectedTab === tab ? 0 : -1}
            $selected={selectedTab === tab}
            onClick={(): void => setSelectedTab(tab)}
            onKeyDown={(event): void => handleTabKeyDown(event, tab)}
          >
            {tab === 'visualization'
              ? translate('Visualization')
              : translate('Data')}
          </Tab>
        ))}
      </TabList>
      <TabPanel
        role="tabpanel"
        id={panelId(selectedTab)}
        aria-labelledby={tabId(selectedTab)}
        tabIndex={0}
      >
        {selectedTab === 'visualization' &&
        chartSpec &&
        hasVisualization(chartSpec, artifact.dataset) ? (
          renderVisualization ? (
            renderVisualization(chartSpec, artifact.dataset)
          ) : (
            <ReportingChartRenderer
              chartSpec={chartSpec}
              dataset={artifact.dataset}
              presentation={presentation}
              fitToContainer={fitToContainer}
              showFallbackCaption={!isEmbedded}
            />
          )
        ) : (
          <ReportingDataTable
            chartSpec={chartSpec}
            dataset={artifact.dataset}
            hideCaption={isEmbedded}
          />
        )}
      </TabPanel>
    </>
  )

  if (isEmbedded) return content

  return (
    <CanvasShell aria-label={translate('Report canvas')}>
      {header && <CanvasHeader>{header}</CanvasHeader>}
      {content}
    </CanvasShell>
  )
}

export { ReportingCanvas }
export default ReportingCanvas
